import { MaybeRefOrGetter } from '@vueuse/core';

export type RubricateKinds = {
  kind: 'initials' | 'manuscript'
};

export type RubricateDocumentPositions = {
  page: string | number,
  pageOffset?: number[],
  position?: number[],
  fromApi?: boolean,
};

export type RubricateConditionsType = RubricateKinds & {
  pages: RubricateDocumentPositions[],
};

export type RubricateSchema = {
  filled: boolean,
  evidenceKey: string | null,
  key: string,
  auth: 'initials' | 'manuscript',
  documentKey: string,
  documentName: string,
  rubricatePages: string,
  rubricateConditions: RubricateConditionsType,
};

export type PagesWithRubricates = RubricateDocumentPositions & RubricateKinds;

function filterDuplicateKeys(array: string[]) {
  return array.filter((item, index) => array.indexOf(item) === index);
}

function mapPages({ rubricateConditions }: RubricateSchema): PagesWithRubricates[] {
  return rubricateConditions.pages.map((pageInfo) => ({ ...pageInfo, kind: rubricateConditions.kind }));
}

function mapRubricateDocuments(_rubricates: ComputedRef<RubricateSchema[]>) {
  const rubricates = computed(() => toValue(_rubricates));

  return computed(() => {
    const rubricatesKey = rubricates.value.map(({ documentKey }) => documentKey);

    return filterDuplicateKeys(rubricatesKey).map((key) => {
      const rubricateInfo = rubricates.value
        .filter(({ documentKey }) => documentKey === key)
        .flatMap(mapPages);

      return { key, rubricateConditions: rubricateInfo };
    });
  });
}

export function useRubricate(_requirements: MaybeRefOrGetter<RubricateSchema[]>, _documentKey: MaybeRefOrGetter<string>) {
  const documentKey = computed(() => toValue(_documentKey));
  const requirements = computed(() => toValue(_requirements));

  const documentsToRubricate = mapRubricateDocuments(requirements);
  const rubricatesInDocument = computed(() => documentsToRubricate.value.find(({ key }) => key === documentKey.value)?.rubricateConditions ?? []);
  const rubricateTotal = computed(() => documentsToRubricate.value.length);

  const documentIndex = computed<number>(() => documentsToRubricate.value.findIndex(({ key }) => key === documentKey.value));

  const positionedKinds = computed(() => {
    const data = documentsToRubricate.value.reduce((acc, { rubricateConditions }) => {
      const conditions = rubricateConditions.map(({ kind }) => kind);

      return [...acc, ...conditions];
    }, [] as Array<'initials' | 'manuscript'>);

    return [...new Set(data)] as Array<'initials' | 'manuscript'>;
  });

  return {
    rubricateTotal,
    rubricatesInDocument,
    documentsToRubricate,
    documentIndex,
    positionedKinds,
  };
}
